// Analytics utility for tracking user interactions and page views

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// Google Analytics configuration - use Vite's import.meta.env
export const GA_TRACKING_ID = import.meta.env.VITE_GA_TRACKING_ID || '';

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window !== 'undefined' && GA_TRACKING_ID) {
    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.gtag = function() {
      (window.gtag as any).q = (window.gtag as any).q || [];
      (window.gtag as any).q.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', GA_TRACKING_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};

// Track page views
export const trackPageView = (url: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: url,
      page_title: title || document.title,
    });
  }
};

// Track custom events
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track project interactions
export const trackProjectView = (projectTitle: string, projectType: string) => {
  trackEvent('project_view', 'Projects', `${projectTitle} (${projectType})`);
};

export const trackProjectClick = (projectTitle: string, clickType: 'github' | 'demo' | 'live') => {
  trackEvent('project_click', 'Projects', `${projectTitle} - ${clickType}`);
};

// Track article interactions
export const trackArticleView = (articleTitle: string) => {
  trackEvent('article_view', 'Articles', articleTitle);
};

export const trackArticleClick = (articleTitle: string) => {
  trackEvent('article_click', 'Articles', articleTitle);
};

// Track skill interactions
export const trackSkillHover = (skillName: string) => {
  trackEvent('skill_hover', 'Skills', skillName);
};

// Track contact interactions
export const trackContactAttempt = (method: 'email' | 'linkedin' | 'form') => {
  trackEvent('contact_attempt', 'Contact', method);
};

// Track download events
export const trackDownload = (fileName: string, fileType: string) => {
  trackEvent('file_download', 'Downloads', `${fileName} (${fileType})`);
};

// Track scroll depth
export const trackScrollDepth = (depth: number) => {
  trackEvent('scroll_depth', 'Engagement', `${depth}%`);
};

// Track time on page
export const trackTimeOnPage = (timeInSeconds: number) => {
  trackEvent('time_on_page', 'Engagement', `${timeInSeconds}s`);
};

// Track mobile vs desktop usage
export const trackDeviceType = () => {
  const isMobile = window.innerWidth < 768;
  trackEvent('device_type', 'Technical', isMobile ? 'mobile' : 'desktop');
};

// Track browser information
export const trackBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browser = 'unknown';
  
  if (userAgent.includes('Chrome')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari')) browser = 'Safari';
  else if (userAgent.includes('Edge')) browser = 'Edge';
  
  trackEvent('browser_type', 'Technical', browser);
};

// Track performance metrics
export const trackPerformance = () => {
  if ('performance' in window) {
    const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (perfData) {
      const loadTime = perfData.loadEventEnd - perfData.loadEventStart;
      trackEvent('page_load_time', 'Performance', `${Math.round(loadTime)}ms`);
    }
  }
};

// Track form submissions
export const trackFormSubmission = (formName: string, success: boolean) => {
  trackEvent('form_submission', 'Forms', `${formName} - ${success ? 'success' : 'error'}`);
};

// Track external link clicks
export const trackExternalLink = (url: string, linkText: string) => {
  trackEvent('external_link_click', 'External Links', `${linkText} - ${url}`);
};

// Track search queries
export const trackSearch = (query: string, resultsCount: number) => {
  trackEvent('search', 'Search', query, resultsCount);
};

// Track video interactions
export const trackVideoInteraction = (videoTitle: string, action: 'play' | 'pause' | 'complete') => {
  trackEvent('video_interaction', 'Videos', `${videoTitle} - ${action}`);
};

// Track section views (for scroll-triggered analytics)
export const trackSectionView = (sectionName: string) => {
  trackEvent('section_view', 'Navigation', sectionName);
};

// Initialize all tracking
export const initializeAnalytics = () => {
  initGA();
  
  // Track initial page load
  trackPageView(window.location.pathname);
  trackDeviceType();
  trackBrowserInfo();
  trackPerformance();
  
  // Track scroll depth
  let maxScrollDepth = 0;
  const trackScroll = () => {
    const scrollDepth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
    if (scrollDepth > maxScrollDepth) {
      maxScrollDepth = scrollDepth;
      if (scrollDepth % 25 === 0) { // Track at 25%, 50%, 75%, 100%
        trackScrollDepth(scrollDepth);
      }
    }
  };
  
  window.addEventListener('scroll', trackScroll);
  
  // Track time on page
  const startTime = Date.now();
  window.addEventListener('beforeunload', () => {
    const timeOnPage = Math.round((Date.now() - startTime) / 1000);
    trackTimeOnPage(timeOnPage);
  });
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackProjectView,
  trackProjectClick,
  trackArticleView,
  trackArticleClick,
  trackSkillHover,
  trackContactAttempt,
  trackDownload,
  trackScrollDepth,
  trackTimeOnPage,
  trackDeviceType,
  trackBrowserInfo,
  trackPerformance,
  trackFormSubmission,
  trackExternalLink,
  trackSearch,
  trackVideoInteraction,
  trackSectionView,
  initializeAnalytics,
};